import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@services/api-client'
import { SNA_PREFIX } from '@services/utils'

import type {
  AnalyticsBreakdownDimension,
  AnalyticsGranularity,
  EventAnalyticsBreakdown,
  EventAnalyticsEventsList,
  EventAnalyticsSummary,
  EventAnalyticsTimeseries
} from '../types'

export interface EventAnalyticsRangeParams {
  from: string
  to: string
}

function rangeKey(params: EventAnalyticsRangeParams) {
  return [params.from, params.to] as const
}

export function useEventAnalyticsSummary(params: EventAnalyticsRangeParams) {
  return useQuery({
    queryKey: ['event-analytics', 'summary', ...rangeKey(params)],
    queryFn: async () => {
      const { data } = await apiClient.get<EventAnalyticsSummary>(
        `${SNA_PREFIX}event-analytics/summary`,
        { params }
      )
      return data
    }
  })
}

export function useEventAnalyticsTimeseries(
  params: EventAnalyticsRangeParams & {
    granularity?: AnalyticsGranularity
    event?: string
  }
) {
  return useQuery({
    queryKey: [
      'event-analytics',
      'timeseries',
      ...rangeKey(params),
      params.granularity ?? 'day',
      params.event ?? null
    ],
    queryFn: async () => {
      const { data } = await apiClient.get<EventAnalyticsTimeseries>(
        `${SNA_PREFIX}event-analytics/timeseries`,
        {
          params: {
            from: params.from,
            to: params.to,
            granularity: params.granularity ?? 'day',
            ...(params.event ? { event: params.event } : {})
          }
        }
      )
      return data
    }
  })
}

export function useEventAnalyticsBreakdown(
  params: EventAnalyticsRangeParams & {
    dimension: AnalyticsBreakdownDimension
  }
) {
  return useQuery({
    queryKey: [
      'event-analytics',
      'breakdown',
      ...rangeKey(params),
      params.dimension
    ],
    queryFn: async () => {
      const { data } = await apiClient.get<EventAnalyticsBreakdown>(
        `${SNA_PREFIX}event-analytics/breakdown`,
        {
          params: {
            from: params.from,
            to: params.to,
            dimension: params.dimension
          }
        }
      )
      return data
    }
  })
}

export function useEventAnalyticsEvents(
  params: EventAnalyticsRangeParams & {
    limit?: number
    offset?: number
    event?: string
  }
) {
  return useQuery({
    queryKey: [
      'event-analytics',
      'records',
      ...rangeKey(params),
      params.limit ?? 50,
      params.offset ?? 0,
      params.event ?? null
    ],
    queryFn: async () => {
      const { data } = await apiClient.get<EventAnalyticsEventsList>(
        `${SNA_PREFIX}event-analytics/records`,
        {
          params: {
            from: params.from,
            to: params.to,
            limit: params.limit ?? 50,
            offset: params.offset ?? 0,
            ...(params.event ? { event: params.event } : {})
          }
        }
      )
      return data
    }
  })
}
